import { Injectable } from '@angular/core'
import { Actions, Effect, ofType } from '@ngrx/effects'
import { catchError, concatMap, map, switchMap, tap } from 'rxjs/operators'
import { of } from 'rxjs'
import {
  AddInvoiceRule,
  AddInvoiceRuleComplete,
  AddInvoiceRuleCondition,
  AddInvoiceRuleConditionComplete,
  AddInvoiceRulesComplete,
  ArchiveInvoiceRule,
  ArchiveInvoiceRuleComplete,
  DeleteInvoiceRule,
  DeleteInvoiceRuleComplete,
  DeleteInvoiceRuleCondition,
  DeleteInvoiceRuleConditionComplete,
  DeleteInvoiceRules,
  DeleteInvoiceRulesComplete,
  DuplicateInvoiceRule,
  LoadInvoiceRules,
  LoadInvoiceRulesComplete,
  PauseInvoiceRule,
  PauseInvoiceRuleComplete,
  PauseInvoiceRuleReminder,
  PauseInvoiceRuleReminderComplete,
  ResumeInvoiceRule,
  ResumeInvoiceRuleComplete,
  ResumeInvoiceRuleReminder,
  ResumeInvoiceRuleReminderComplete,
  UnArchiveInvoiceRule,
  UnArchiveInvoiceRuleComplete,
  UpdateInvoiceRule,
  UpdateInvoiceRuleComplete,
  UpdateInvoiceRuleCondition,
  UpdateInvoiceRuleConditionComplete,
} from './rule.actions'
import { InvoiceRuleService } from './rule.service'
import * as R from 'ramda'
import { isSomething, Toaster } from '@tokeet/tv3-platform'
import { ActionFailed, TriggerEvents } from '@tokeet-frontend/utility'
import { InvoiceRuleCreateRequest, InvoiceRuleUpdateRequest } from './rule.model'

@Injectable()
export class InvoiceRuleEffects {
  @Effect()
  addInvoiceRule$ = this.actions$.pipe(
    ofType(AddInvoiceRule),
    concatMap(({ payload, silent }) => {
      const request: InvoiceRuleCreateRequest = {
        event: payload.event,
        timeevent: payload.timeevent.event,
        days: payload.timeevent.days,
        hours: payload.timeevent.hours,
        status: 1,
        handler: 'Handlers::InvoiceRuleHandler',
        service: 'invoicing',
        conditions: payload.conditions,
        attributes: {
          name: payload.name,
          tags: [],
          charge: payload.charge,
          send: payload.send,
          mark: payload.mark,
          failed: payload.failedReminder,
          reminder: payload.reminder
            ? {
                when: payload.reminderWhen,
                days: payload.reminderDays,
              }
            : null,
        },
        not_rentals: payload.not_rentals,
        channels: R.isEmpty(payload.channels) ? [''] : payload.channels,
        rentals: R.isEmpty(payload.rentals) ? [''] : payload.rentals,
        users: R.append(111, payload.users),
        template: payload.template,
      }
      if (
        (isSomething(payload.not_rentals) && !isSomething(payload.channels)) ||
        (!isSomething(payload.channels) && !isSomething(payload.rentals))
      ) {
        return this.invoiceRuleService.create(request).pipe(
          map((response) => AddInvoiceRuleComplete({ rule: response, silent })),
          tap(() => this.toaster.success($localize`Invoice rule created successfully.`)),
          catchError((error) => of(ActionFailed({ error })))
        )
      } else {
        return this.invoiceRuleService.createMany(request).pipe(
          map((response) => AddInvoiceRulesComplete({ rules: response, silent })),
          tap(() => this.toaster.success($localize`Invoice rule created successfully.`)),
          catchError((error) => of(ActionFailed({ error })))
        )
      }
    })
  )
  @Effect()
  updateInvoiceRule$ = this.actions$.pipe(
    ofType(UpdateInvoiceRule),
    concatMap(({ payload, pkey, silent }) => {
      const request: InvoiceRuleUpdateRequest = {
        pkey,
        event: payload.event,
        timeevent: TriggerEvents.isTime(payload.event) ? payload.timeevent.event : payload.event,
        days: payload.timeevent.days,
        hours: payload.timeevent.hours,
        status: 1,
        handler: 'Handlers::InvoiceRuleHandler',
        service: 'invoicing',
        conditions: payload.conditions,
        attributes: {
          name: payload.name,
          tags: payload.tags,
          charge: payload.charge,
          send: payload.send,
          mark: payload.mark,
          failed: payload.failedReminder,
          reminder: payload.reminder
            ? {
                when: payload.reminderWhen,
                days: payload.reminderDays,
              }
            : null,
        },
        not_rentals: payload.not_rentals,
        channel: payload.channels,
        rental: payload.rentals,
        users: R.append(111, payload.users),
        template: payload.template,
      }
      return this.invoiceRuleService.update(request).pipe(
        map((result) => UpdateInvoiceRuleComplete({ update: { id: result.pkey, changes: result }, silent })),
        tap(() => this.toaster.success($localize`Invoice rule updated successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  duplicateInvoiceRule$ = this.actions$.pipe(
    ofType(DuplicateInvoiceRule),
    concatMap(({ rule, name }) => {
      const request: InvoiceRuleCreateRequest = {
        event: rule.event,
        timeevent: rule.timeevent,
        days: rule.settings.days,
        hours: rule.settings.hours,
        status: 1,
        handler: 'Handlers::InvoiceRuleHandler',
        service: 'invoicing',
        conditions: rule.conditions,
        attributes: {
          ...rule.attributes,
          name,
        },
        not_rentals: R.defaultTo([], rule.settings.not_rentals),
        channels: [rule.settings.channel],
        rentals: [rule.settings.rental],
        users: R.append(111, rule.users),
        template: rule.template,
      }
      return this.invoiceRuleService.create(request).pipe(
        map((result) => AddInvoiceRuleComplete({ rule: result })),
        tap(() => this.toaster.success($localize`Invoice rule updated successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    })
  )

  @Effect()
  loadInvoiceRules$ = this.actions$.pipe(
    ofType(LoadInvoiceRules),
    concatMap(() =>
      this.invoiceRuleService.all().pipe(
        map((rules) => LoadInvoiceRulesComplete({ rules })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteInvoiceRules$ = this.actions$.pipe(
    ofType(DeleteInvoiceRules),
    concatMap(({ ids }) =>
      this.invoiceRuleService.deleteBatch(ids).pipe(
        map(() => DeleteInvoiceRulesComplete({ ids })),
        tap(() => this.toaster.success($localize`Invoice rules deleted successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  pauseRuleReminder$ = this.actions$.pipe(
    ofType(PauseInvoiceRuleReminder),
    concatMap(({ id }) =>
      this.invoiceRuleService.pauseRuleReminder(id).pipe(
        map((rule) => PauseInvoiceRuleReminderComplete({ update: { id: rule.pkey, changes: rule } })),
        tap(() => this.toaster.success($localize`Invoice rule reminder paused successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  resumeRuleReminder$ = this.actions$.pipe(
    ofType(ResumeInvoiceRuleReminder),
    concatMap(({ id, when, days }) =>
      this.invoiceRuleService.resumeRuleReminder(id, when, days).pipe(
        map((rule) => ResumeInvoiceRuleReminderComplete({ update: { id: rule.pkey, changes: rule } })),
        tap(() => this.toaster.success($localize`Invoice rule reminder resumed successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteInvoiceRule$ = this.actions$.pipe(
    ofType(DeleteInvoiceRule),
    concatMap(({ id }) =>
      this.invoiceRuleService.delete(id).pipe(
        map(() => DeleteInvoiceRuleComplete({ id })),
        tap(() => this.toaster.success($localize`Invoice rule deleted successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  pauseInvoiceRule$ = this.actions$.pipe(
    ofType(PauseInvoiceRule),
    concatMap(({ id }) =>
      this.invoiceRuleService.pause(id).pipe(
        map(() => PauseInvoiceRuleComplete({ update: { id, changes: { status: 0 } } })),
        tap(() => this.toaster.success($localize`Invoice rule paused successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  resumeInvoiceRule$ = this.actions$.pipe(
    ofType(ResumeInvoiceRule),
    concatMap(({ id }) =>
      this.invoiceRuleService.resume(id).pipe(
        map(() => ResumeInvoiceRuleComplete({ update: { id, changes: { status: 1 } } })),
        tap(() => this.toaster.success($localize`Invoice rule resumed successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  archiveInvoiceRule$ = this.actions$.pipe(
    ofType(ArchiveInvoiceRule),
    concatMap(({ rule }) =>
      this.invoiceRuleService.pause(rule.pkey).pipe(
        concatMap(() =>
          this.invoiceRuleService.archive(rule.pkey).pipe(
            tap(() => this.toaster.success($localize`Invoice rule archived successfully.`)),
            concatMap(() => [
              PauseInvoiceRuleComplete({ update: { id: rule.pkey, changes: { status: 0 } } }),
              ArchiveInvoiceRuleComplete({ update: { id: rule.pkey, changes: { archived: 1 } } }),
            ]),
            catchError((error) => of(ActionFailed({ error })))
          )
        )
      )
    )
  )

  @Effect()
  unArchiveInvoiceRule$ = this.actions$.pipe(
    ofType(UnArchiveInvoiceRule),
    concatMap(({ rule }) =>
      this.invoiceRuleService.unarchive(rule.pkey).pipe(
        map(() => UnArchiveInvoiceRuleComplete({ update: { id: rule.pkey, changes: { archived: 0 } } })),
        tap(() => this.toaster.success($localize`Invoice rule unarchived successfully.`)),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  createCondition$ = this.actions$.pipe(
    ofType(AddInvoiceRuleCondition),
    switchMap(({ request }) =>
      this.invoiceRuleService.createCondition(request).pipe(
        tap(() => this.toaster.success($localize`Invoice rule condition created successfully.`)),
        map((condition) =>
          AddInvoiceRuleConditionComplete({
            response: {
              ruleId: request.ruleId,
              condition,
            },
          })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  saveCondition$ = this.actions$.pipe(
    ofType(UpdateInvoiceRuleCondition),
    switchMap(({ request }) =>
      this.invoiceRuleService.saveCondition(request).pipe(
        tap(() => this.toaster.success($localize`Invoice rule condition saved successfully.`)),
        map((condition) =>
          UpdateInvoiceRuleConditionComplete({
            response: {
              ruleId: request.ruleId,
              condition,
            },
          })
        ),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  @Effect()
  deleteCondition$ = this.actions$.pipe(
    ofType(DeleteInvoiceRuleCondition),
    switchMap(({ request }) =>
      this.invoiceRuleService.deleteCondition(request).pipe(
        tap(() => this.toaster.success($localize`Invoice rule condition deleted successfully.`)),
        map(() => DeleteInvoiceRuleConditionComplete({ response: request })),
        catchError((error) => of(ActionFailed({ error })))
      )
    )
  )

  constructor(private actions$: Actions, private toaster: Toaster, private invoiceRuleService: InvoiceRuleService) {}
}
